import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import "../../common/game.css";
import appStore from "../../storage/AppStore";
import Btn from "../../ui/Btn";
import clubStore from "../../storage/ClubStore";
import systemStore from "../../storage/SystemStore";

const SideAvatar = ({logo, name}) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="side-avatar">
      {logo && !failed ? <img src={logo} alt={name} className="avatar-img" loading="lazy" onError={() => setFailed(true)}/> : <div className="avatar-fallback"/>}
    </div>
  );
};

const GamesList = ({games = [], detailsBasePath = "/game", role = "MGR"}) => {
  const nav = useNavigate();

  const safeLogo = (url) => (typeof url === "string" && url.trim().length ? url : null);
  const hasResult = (item) => !!(item?.result?.final && item.result.final.side1 != null && item.result.final.side2 != null);
  const getStatusBadge = (item) => (item?.result?.status != null ? item.result.status : "upcoming");
  const getSideMode = (item) => (item.game_team_1_id || item.game_team_2_id ? "teams" : "players");

  const getSide1Name = (item) => {
    const mode = getSideMode(item);
    return mode === "teams" ? (item.game_team_1?.name || "-") : (item.player_1?.title || "-");
  };

  const getSide2Name = (item) => {
    const mode = getSideMode(item);
    return mode === "teams" ? (item.game_team_2?.name || "-") : (item.player_2?.title || "-");
  };

  const getSide1Logo = (item) => safeLogo(item.company?.logo);
  const getSide2Logo = (item) => safeLogo(item.guest_company?.logo);
  const onOpenDetails = (item) => nav(`${detailsBasePath}/${item.id}`);

  const renderResult = (item) => {
    if (!hasResult(item)) return null;
    const {side1, side2} = item.result.final || {};
    const winner = item.result.winner;
    return (
      <div className="game-result" title="Result">
        <span className={`score ${winner === "side1" ? "winner" : ""}`}>{side1}</span>
        <span className="score-sep">:</span>
        <span className={`score ${winner === "side2" ? "winner" : ""}`}>{side2}</span>
      </div>
    );
  };

  const renderBadges = (item) => {
    const status = getStatusBadge(item);
    return (
      <div className="game-badges">
        <span className={`badge badge-${status}`}>{appStore.tr[status]}</span>
        {item.tournament?.name ? (
          <span className="badge badge-tournament" title={item.tournament.name}>
            <i className="pi pi-sitemap" aria-hidden="true"/>{item.tournament.name}
          </span>
        ) : (
          <span className="badge badge-tournament" title={appStore.tr.friendly_match}>
            <i className="pi pi-users" aria-hidden="true"/>{appStore.tr.friendly_match}
          </span>
        )}
        {item.fee != null && String(item.fee) !== "" && (
          <span className="badge badge-fee" title="Fee"><i className="pi pi-wallet" aria-hidden="true"/>{item.fee}</span>
        )}
        {item.has_awards && (
          <span className="badge badge-award" title={appStore.tr.awards || "Awards"}>
            <i className="pi pi-star" aria-hidden="true"/>{item.awards_count}
          </span>
        )}
      </div>
    );
  };

  const GameCard = ({item}) => {
    const s1Name = getSide1Name(item);
    const s2Name = getSide2Name(item);
    const s1Logo = getSide1Logo(item);
    const s2Logo = getSide2Logo(item);

    return (
      <div className="game-card h-full flex flex-column">
        <div className="game-card-clickable flex-grow-1 flex flex-column" role="button" tabIndex={0}
          onClick={() => onOpenDetails(item)} onKeyDown={(e) => e.key === "Enter" && onOpenDetails(item)}>
          <div className="game-card-top align-items-start">
            <div className="game-meta flex-grow-1">
              <div className="font-semibold text-lg line-height-3 mb-1">{item.name}</div>
              <div className="game-date flex align-items-center gap-2">
                <i className="pi pi-calendar text-primary" aria-hidden="true"/>
                <span>{item.start_time}</span>
              </div>
              {item.address && (
                <div className="game-address" title={item.address}>
                  <i className="pi pi-map-marker mr-1" aria-hidden="true"/>{item.address}
                </div>
              )}
            </div>
            {hasResult(item) && <div className="flex-shrink-0">{renderResult(item)}</div>}
          </div>

          <div className="game-card-mid surface-ground border-round-lg p-3 my-2">
            <div className="side side1">
              <SideAvatar logo={s1Logo} name={s1Name}/>
              <div className="side-name" title={s1Name}>{s1Name}</div>
            </div>
            <div className="vs">VS</div>
            <div className="side side2">
              <SideAvatar logo={s2Logo} name={s2Name}/>
              <div className="side-name" title={s2Name}>{s2Name}</div>
            </div>
          </div>

          <div className="game-card-bottom mt-auto pt-2">
            {renderBadges(item)}
            <i className="pi pi-chevron-right text-color-secondary flex-shrink-0" aria-hidden="true"/>
          </div>
        </div>

        <div className="game-card-actions flex-wrap align-items-center gap-2 border-top-1 surface-border mt-2">
          {item.company_id === clubStore.club.id ? (
            role === "MGR" && (
              <>
                <Btn icon="pi pi-pencil" severity="secondary" onClick={() => nav(`/manager/games/edit/${item.id}`)}/>
                <Btn icon="pi pi-users" severity="info" onClick={() => nav(`/manager/games/participants/${item.id}`)}/>
                {item.fee > 0 && <Btn icon="pi pi-dollar" severity="success" onClick={() => nav(`/manager/games/billing/${item.id}`)}/>}
                <Btn icon="pi pi-megaphone" severity="warning" onClick={() => {
                  clubStore.informGame(item.id)
                    .then(() => systemStore.setSuccess(appStore.tr.started_informing))
                    .catch(err => systemStore.setError(err.message));
                }}/>
              </>
            )
          ) : (
            <Btn icon="pi pi-users" severity="info" onClick={() => nav(`/manager/games/game/team/${item.game_team_2_id}`)}/>
          )}
          {item.has_media && (
            <Btn icon="pi pi-video" label={appStore.tr.media} severity="help" onClick={() => {
              clubStore.sendGameMedia(item.id).then(() => systemStore.setSuccess(appStore.tr.media_sent_to_telegram));
            }}/>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="grid m-0">
      {games?.map((g) => (
        <div className="col-12 lg:col-6 p-2" key={g.id}>
          <GameCard item={g}/>
        </div>
      ))}
    </div>
  );
};

export default GamesList;
